export type SamplePrompt = {
  n: number;
  title: string;
  text: string;
  tips: string[];
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
  { name: 'DeepSeek', url: () => 'https://chat.deepseek.com/' },
];

export const demoOutput = {
  title: 'CONCLUSIONS EN DÉFENSE — [Nom de l’affaire]',
  sections: [
    { heading: 'Rappel des faits et de la procédure', lines: ['Exposé neutre et chronologique tiré uniquement des pièces du dossier', 'Aucune qualification · seulement les faits établis'] },
    { heading: 'Discussion', lines: ['Chaque moyen développé, élément par élément'] },
    { heading: 'Par ces motifs', lines: ['Demandes formulées de manière claire et précise'] },
  ],
};

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Conclusions en défense',
    text: `Agis comme un Avocat plaidant senior, spécialisé dans la rédaction technique d'actes de procédure devant les juridictions françaises. Ton objectif est de rédiger des « Conclusions en défense » professionnelles, persuasives et procéduralement solides, pouvant être déposées après relecture par l'avocat.

Ouvre avec l'en-tête identifiant la [Juridiction et Chambre], le [Numéro RG de l'affaire], la [Partie défenderesse] et la [Partie demanderesse]. Le ton doit être formel, mesuré et précis, sans excès rhétorique susceptible de nuire à la crédibilité devant le tribunal.

Rédige une introduction concise exposant l'objet des conclusions et le fondement procédural principal, tel que [Moyen principal : ex. Irrecevabilité, Prescription, Incompétence].

Rédige la partie « Rappel des faits et de la procédure » strictement à partir des pièces, en résumant les [Faits clés] dans un ordre neutre et chronologique.

Construis la partie « Discussion » avec un titre numéroté pour chaque moyen. Pour chacun, identifie les conditions requises, explique pourquoi la demande adverse ne satisfait pas [Condition manquante ou vice], et rattache ce vice à la demande formulée. Indique toute référence jurisprudentielle comme [JURISPRUDENCE À VÉRIFIER] plutôt que d'inventer une autorité.

Termine par le dispositif « Par ces motifs » demandant [Débouté / Irrecevabilité / Article 700 CPC], suivi du bloc de signature de [Nom de l'avocat, Barreau, Cabinet].

S'il manque des informations clés pour compléter les champs entre crochets, pose-moi les questions nécessaires avant de répondre.

Important : n'invente pas de références, de numéros d'affaires, de décisions ou de citations. Si tu ne peux pas les vérifier avec des sources réelles, indique-le clairement.`,
    tips: ['Vérifiez les règles de forme et de délai applicables devant la juridiction saisie avant tout dépôt.'],
  },
  {
    n: 2,
    title: 'Analyse des risques contractuels',
    text: `Agis comme un Avocat d'affaires senior, doté d'une solide expérience en contrats commerciaux et en allocation des risques. Ta mission est de réaliser une analyse structurée des risques du contrat que je te fournis et de remettre une note claire et hiérarchisée pour [Nom du client].

Commence par identifier le [Type de contrat], les [Parties et leurs rôles], le [Droit applicable] et les [Durée et conditions de renouvellement]. Résume l'objet économique de l'opération en deux phrases.

Examine le contrat clause par clause, en te concentrant sur les garanties, la limitation de responsabilité, la résiliation, la propriété intellectuelle, la confidentialité, les conditions de paiement et le règlement des litiges. Pour chaque clause, indique si elle est [Favorable, Neutre ou Défavorable] à mon client et explique pourquoi.

Crée un « Registre des risques » sous forme de liste numérotée. Pour chaque risque, indique la référence de la clause, un niveau de gravité [Élevé / Moyen / Faible], l'exposition potentielle et une proposition de reformulation en langage contractuel clair.

Signale toute clause manquante qu'un avocat prudent s'attendrait à trouver dans ce type de contrat, compte tenu de la position de mon client comme [Acheteur / Vendeur / Concédant / Licencié].

Conclus par une courte stratégie de négociation listant les trois points prioritaires et les concessions raisonnables que mon client pourrait proposer.

S'il manque des informations clés pour compléter les champs entre crochets, pose-moi les questions nécessaires avant de répondre.`,
    tips: [
      "Supprimez ou anonymisez toute information identifiant le client avant de coller le contrat dans un outil d'IA.",
      'Vérifiez chaque reformulation proposée au regard du droit applicable indiqué.',
    ],
  },
  {
    n: 3,
    title: 'Mise en demeure',
    text: `Agis comme un Conseiller persuasif et avocat expérimenté en contentieux civil. Ta mission est de rédiger une « Mise en demeure » ferme et professionnelle au nom de [Nom du client], adressée à [Nom et adresse du destinataire].

Ouvre avec la date, un objet clair faisant référence à [Description du dossier], la mention « Lettre recommandée avec accusé de réception » et l'indication que le cabinet représente le client dans ce dossier.

Expose les faits dans un récit concis et chronologique : ce qui s'est passé le [Date des faits], la [Nature du manquement ou du préjudice], et les pièces qui fondent la réclamation [Pièces justificatives].

Explique le fondement juridique de la responsabilité dans un français clair, en faisant référence à [Fondement : ex. Inexécution contractuelle, Responsabilité délictuelle] sans exagérer la portée du droit. Indique toute référence légale ou jurisprudentielle comme [RÉFÉRENCE À VÉRIFIER].

Détaille les sommes réclamées, notamment [Montant du préjudice], et la méthode de calcul de chaque montant.

Formule clairement la demande : le paiement ou l'action exigée, le délai [Délai de réponse], et les suites envisagées en cas d'inaction, en conservant un ton professionnel et sans menace contraire à la déontologie.

Termine par le bloc de signature de l'avocat [Nom de l'avocat, Cabinet, Coordonnées].

S'il manque des informations clés pour compléter les champs entre crochets, pose-moi les questions nécessaires avant de répondre.`,
    tips: ['Vérifiez les règles déontologiques applicables aux correspondances avec la partie adverse avant l’envoi.'],
  },
];
